/**
 * buildVersionCheck.ts
 *
 * 2026-05-17: iOS Safari PWA mode の index.html cache 問題対応。
 *
 * 動作:
 *   1. bundle に inline された BUILD_VERSION (REACT_APP_BUILD_ID、build 時 timestamp) を読む
 *   2. 起動時に /version.json を no-cache fetch (cache bust query 付き)
 *   3. server 側 buildId と bundle 側 BUILD_VERSION を比較
 *   4. 不一致 = 古い JS が動いてる → window.location.reload() で新 bundle 強制取得
 *
 * セッション内再 reload 防止:
 *   reload 後も版違いが続く (CDN propagation lag 等) ケースで無限 loop 防止のため
 *   sessionStorage に reload 済 flag を保存、同 session 内では 1 度だけ reload する。
 *
 * dev 環境 (BUILD_VERSION=undefined or 'dev') ではスキップ。
 *
 * Codex 助言の Phase 4 「症状の可視化」の一環としての健全性 check + 自己修復。
 */

const BUILD_VERSION: string = process.env.REACT_APP_BUILD_ID || '';

const STORAGE_KEY_RELOAD_DONE = 'weborder:build-reload-done';

interface VersionResponse {
  buildId?: string;
  buildIso?: string;
}

/**
 * 起動時に呼ぶ。古い bundle が走ってる場合だけ reload する。
 * 非同期、boot をブロックしない。
 */
export async function checkBuildVersion(): Promise<void> {
  if (!BUILD_VERSION) {
    // dev 環境 (REACT_APP_BUILD_ID が無い) — スキップ
    return;
  }
  if (typeof window === 'undefined') return;
  if (typeof fetch !== 'function') return;

  try {
    // cache bust query で確実に新 version.json を取る
    const url = `/version.json?t=${Date.now()}`;
    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return;
    const data: VersionResponse = await res.json();
    if (!data || !data.buildId) return;

    if (data.buildId === BUILD_VERSION) {
      // 同 version OK — 過去の reload flag 消去 (次回 deploy で再度 trigger 可能に)
      try {
        window.sessionStorage.removeItem(STORAGE_KEY_RELOAD_DONE);
      } catch {
        /* ignore */
      }
      return;
    }

    // ── 不一致 = 古い bundle 検出 ──
    // 無限 loop 防止: 同 session で 1 度だけ reload
    let alreadyReloaded = false;
    try {
      alreadyReloaded = window.sessionStorage.getItem(STORAGE_KEY_RELOAD_DONE) === '1';
    } catch {
      /* ignore */
    }

    if (alreadyReloaded) {
      // eslint-disable-next-line no-console
      console.warn(
        `[buildVersionCheck] stale bundle still detected after reload: ` +
        `bundle=${BUILD_VERSION}, server=${data.buildId}. ` +
        `CDN propagation lag の可能性、reload せず継続。`
      );
      return;
    }

    try {
      window.sessionStorage.setItem(STORAGE_KEY_RELOAD_DONE, '1');
    } catch {
      /* ignore */
    }

    // eslint-disable-next-line no-console
    console.warn(
      `[buildVersionCheck] stale bundle detected. Reloading... ` +
      `bundle=${BUILD_VERSION}, server=${data.buildId}`
    );

    // 強制 reload (modern browsers は cache validators を check する、
    // 結果として index.html を re-fetch → 新 JS bundle を fetch する)
    window.location.reload();
  } catch (e) {
    // version.json fetch 失敗は致命でない、握り潰す
    // eslint-disable-next-line no-console
    console.warn('[buildVersionCheck] version check failed:', e);
  }
}

/**
 * 現在の bundle build version を返す (debug / UI 表示用、診断ログでも使用)。
 */
export function getCurrentBuildVersion(): string {
  return BUILD_VERSION || 'dev';
}
